import { useRef, useState } from "react";
import { Paperclip, Send, X } from "lucide-react";
import { openPendingFile } from "../../../../utils/download";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import type { ConversationType, MentionUser } from "../../types";
import {
  useCreateProjectMessage,
  type ConversationBase,
} from "../../api/conversationsApi";
import { useCommentDraft } from "@/hooks/useCommentDraft";

interface Props {
  projectId: string;
  conversationType: ConversationType;
  mentionUsers: MentionUser[];
  parentId?: string | null;
  onPosted?: () => void;
  placeholder?: string;
  base?: ConversationBase;
}

export function MessageComposer({
  projectId,
  conversationType,
  mentionUsers,
  parentId = null,
  onPosted,
  placeholder = "Napisz wiadomość…",
  base = "projects",
}: Props) {
  // Klucz szkicu niesie kolekcję: ta sama para (id, kanał) może wystąpić i przy
  // przedsięwzięciu, i przy umowie — bez tego nienapisana wiadomość wypłynęłaby
  // w cudzym oknie.
  const storageKey =
    parentId === null
      ? `gg_draft_${base}_${projectId}_${conversationType}`
      : `gg_reply_${parentId}`;
  const [content, setContent, clearDraft] = useCommentDraft(storageKey);

  const [files, setFiles] = useState<File[]>([]);
  const [mentionIds, setMentionIds] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const create = useCreateProjectMessage();

  const removeFile = (i: number) =>
    setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() && files.length === 0) return;

    await create.mutateAsync({
      projectId,
      conversationType,
      content,
      parent: parentId,
      mentionIds,
      files,
      base,
    });

    clearDraft();
    setFiles([]);
    setMentionIds([]);
    onPosted?.();
  };

  const mentionSuggestions = mentionUsers.map((u) => ({ id: u.id, name: u.name }));

  return (
    <form onSubmit={handleSubmit}>
      {/* File previews */}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {files.map((f, i) => (
            <div
              key={i}
              className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg px-2 py-1 text-xs text-gray-700 dark:text-gray-300"
            >
              <Paperclip size={11} className="text-gray-400" />
              <button
                type="button"
                onClick={() => openPendingFile(f)}
                className="max-w-[140px] truncate hover:underline hover:text-[var(--color-primary)]"
                title={`${f.name} — kliknij, aby podejrzeć`}
              >
                {f.name}
              </button>
              <button
                type="button"
                onClick={() => removeFile(i)}
                className="ml-1 text-gray-400 hover:text-red-500"
              >
                <X size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

      <RichTextEditor
        value={content}
        onChange={setContent}
        placeholder={placeholder}
        mentionSuggestions={mentionSuggestions}
        onMentionIdsChange={setMentionIds}
        disabled={create.isPending}
      />

      <div className="flex items-center justify-between mt-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-[var(--color-primary)] transition-colors"
          title="Dodaj załącznik"
        >
          <Paperclip size={13} />
          Załącz plik
        </button>
        <button
          type="submit"
          disabled={create.isPending || (!content.trim() && files.length === 0)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--color-primary)] text-white text-xs font-medium hover:bg-[var(--color-primary-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Send size={12} />
          Wyślij
        </button>
      </div>

      <input
        ref={fileRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files)
            setFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
          e.target.value = "";
        }}
      />
    </form>
  );
}
